import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Toaster } from "react-hot-toast";
import Routes from "./components/routes/routesContainer";
import { UserContext } from "./hooks/UserContext";
import { api } from "./services/baseUrl";

const App = () => {
  const { user, loading } = useContext(UserContext);
  const [siteSettings, setSiteSettings] = useState({
    maintenance_mode: false,
    blocked_roles: [],
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const browserWindow = typeof window !== "undefined" ? window : undefined;

  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await api.get("/site-settings");
      setSiteSettings(
        response.data?.data || { maintenance_mode: false, blocked_roles: [] },
      );
    } catch (error) {
      console.error("Erreur chargement paramètres site:", error);
      setSiteSettings({ maintenance_mode: false, blocked_roles: [] });
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSiteSettings();

    const handleSettingsUpdate = () => {
      fetchSiteSettings();
    };

    browserWindow?.addEventListener(
      "siteSettingsUpdated",
      handleSettingsUpdate,
    );
    return () =>
      browserWindow?.removeEventListener(
        "siteSettingsUpdated",
        handleSettingsUpdate,
      );
  }, [browserWindow, fetchSiteSettings]);

  const currentPath = browserWindow?.location?.pathname || "/";
  const currentRole = Number(user?.id_role ?? user?.role_id ?? 0);
  const isAdmin = currentRole === 3;

  const blockedRoles = useMemo(
    () =>
      Array.isArray(siteSettings?.blocked_roles)
        ? siteSettings.blocked_roles.map(Number)
        : [],
    [siteSettings],
  );

  const maintenanceEnabled = Boolean(siteSettings?.maintenance_mode);
  const roleBlocked =
    !isAdmin && currentRole > 0 && blockedRoles.includes(currentRole);
  const isAdminRoute = currentPath.startsWith("/admin");
  const canBypassMaintenance =
    (Boolean(user) && isAdmin) || isAdminRoute || currentPath === "/login";
  const showMaintenance = !canBypassMaintenance && maintenanceEnabled;

  const handleBlockedLogout = () => {
    localStorage.removeItem("access_token");
    if (browserWindow) {
      browserWindow.location.href = "/";
    }
  };

  if (loading || settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1114] text-white">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-rose-500 border-t-transparent" />
      </div>
    );
  }

  if (showMaintenance || roleBlocked) {
    const g = 'linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)';
    const title = roleBlocked
      ? 'Accès temporairement suspendu'
      : siteSettings?.maintenance_title || 'Site en maintenance';
    const description = roleBlocked
      ? "Votre rôle est temporairement bloqué par l'administration. Merci de réessayer plus tard."
      : siteSettings?.maintenance_message ||
        'Le site est actuellement en maintenance. Merci de revenir dans quelques instants.';

    return (
      <div
        className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden"
        style={{ backgroundColor: '#07070a', color: '#fff' }}
      >
        {/* Orbs */}
        <div
          className="pointer-events-none absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)', filter: 'blur(90px)' }}
        />
        <div
          className="pointer-events-none absolute -bottom-40 -right-40 h-[400px] w-[400px] rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #f43f5e 0%, transparent 70%)', filter: 'blur(80px)' }}
        />

        <div className="relative z-10 w-full max-w-lg text-center">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 mb-8 text-xs font-semibold uppercase tracking-widest"
            style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.10)', color: '#d1d5db' }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: g }}
            />
            Krayne Agency
          </div>

          {/* Icône */}
          <div
            className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl text-4xl"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {roleBlocked ? '⛔' : '🛠️'}
          </div>

          {/* Titre gradient */}
          <h1
            className="text-4xl font-black mb-4"
            style={{
              background: g,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {title}
          </h1>

          {/* Description */}
          <p className="text-base leading-relaxed mb-10" style={{ color: 'rgba(255,255,255,0.60)' }}>
            {description}
          </p>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {user && (
              <button
                type="button"
                onClick={handleBlockedLogout}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-all hover:opacity-80"
                style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
                </svg>
                Se déconnecter
              </button>
            )}
            {!user && currentPath !== '/login' && (
              <a
                href="/login"
                className="inline-flex rounded-full px-6 py-3 text-sm font-semibold text-white transition-all hover:opacity-80"
                style={{ background: g }}
              >
                Connexion
              </a>
            )}
          </div>

          {user && !roleBlocked && (
            <p className="mt-6 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Connecté en tant que <span style={{ color: 'rgba(255,255,255,0.55)' }}>{user.nickname || user.email}</span> — déconnectez-vous puis reconnectez-vous en tant qu'admin
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: "#1a1a1b",
            color: "#fff",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
          success: {
            iconTheme: {
              primary: "#10b981",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <Routes />
    </>
  );
};

export default App;
